import React, { Component, ErrorInfo, ReactNode, useState } from "react";
// Import reactive hooks from the Convex client to communicate with the database
import { useQuery, useAction } from "convex/react";
// Import the generated Convex API schema references
import { api } from "../../../convex/_generated/api";

/**
 * ErrorBoundary Class Component
 *
 * Catches rendering errors, uncaught query exceptions, and state-thrown actions errors,
 * rendering a fallback warning container with data-testid="subscription-status-error"
 * to satisfy robust error-handling constraints (T-404).
 */
export class SubscriptionStatusErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  // Non-coders: This hook captures errors thrown from child components during render
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("SubscriptionStatusErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="subscription-status-error" className="payment-error-alert">
          <h3>Subscription Status Error</h3>
          <p>{this.state.error?.message || "An error occurred while loading your subscription."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Inner component that interacts with the Convex backend hooks.
 * This is wrapped by the SubscriptionStatusErrorBoundary.
 */
const SubscriptionStatusContent: React.FC = () => {
  // Query the user's plan details reactively.
  // Non-coders: This hook automatically fetches data from the database and updates when it changes.
  const planInfo = useQuery(api.users.getUserPlan);

  // Hook to invoke the Stripe Customer Portal redirect link generator.
  const createCustomerPortalSession = useAction(api.stripe.createCustomerPortalSession);

  // State to track if the browser is in the process of redirecting to Stripe
  const [isRedirecting, setIsRedirecting] = useState(false);

  // State to store and display any portal action errors inline (data-testid="portal-error")
  const [portalError, setPortalError] = useState<string | null>(null);

  // If the query is undefined, Convex is still resolving the database read (loading state)
  if (planInfo === undefined) {
    return (
      <div data-testid="subscription-status-loading" className="payment-loading-skeleton">
        <div className="skeleton-field" style={{ height: "48px", width: "100%", marginBottom: "1rem" }}></div>
        <div className="skeleton-field" style={{ height: "120px", width: "100%", marginBottom: "1rem" }}></div>
        <div className="skeleton-field" style={{ height: "40px", width: "150px" }}></div>
      </div>
    );
  }

  // If the query resolves to null, no valid user plan record could be retrieved (empty state)
  if (planInfo === null) {
    return (
      <div data-testid="subscription-status-empty" className="subscription-status-card">
        <h2 className="status-card-title">Subscription Status</h2>
        <p style={{ marginTop: "1rem", color: "var(--color-text-secondary)" }}>
          No subscription details found for your account. Please log in or contact support.
        </p>
      </div>
    );
  }

  // Extract variables from the reactive plan response
  const { plan, trialDaysLeft, subscriptionStatus, currentPeriodEnd } = planInfo;

  /**
   * Click handler for "Manage Billing" button.
   * Invokes the Stripe portal session action, then changes window location to redirect.
   */
  const handleManageBilling = async () => {
    setIsRedirecting(true);
    setPortalError(null);

    try {
      // Execute the Stripe action on the backend
      const result = await createCustomerPortalSession();
      
      if (result && result.url) {
        // Perform external page navigation to the Stripe customer billing dashboard
        window.location.href = result.url;
      } else {
        throw new Error("Unable to retrieve Customer Portal URL.");
      }
    } catch (err) {
      setIsRedirecting(false);
      // Catch rate limits, API rejects, or network errors and display them inline
      const errorMessage = err instanceof Error ? err.message : "Failed to generate billing portal session. Please try again.";
      setPortalError(errorMessage);
    }
  };

  // Format the epoch timestamp to a readable date format
  const formatRenewalDate = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleDateString("en-US", {
      timeZone: "UTC",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Convert internal plan names to user-friendly titles
  const getPlanTitle = (planName: string) => {
    switch (planName) {
      case "free":
        return "Free Tier";
      case "trial":
        return "Free Trial";
      case "pro":
        return "Pro Plan";
      case "team":
        return "Team Plan";
      default:
        return planName.charAt(0).toUpperCase() + planName.slice(1);
    }
  };

  // Map Stripe subscription status to billing.css badge variations
  const getBadgeClass = (status?: string, pName?: string) => {
    if (status === "past_due") return "status-past-due";
    if (status === "trialing" || pName === "trial") return "status-trialing";
    if (status === "active") return "status-active";
    return "status-free";
  };

  return (
    <div className="subscription-status-card">
      <div className="status-card-header">
        <h2 className="status-card-title">Subscription Status</h2>
        <span className={`plan-status-badge ${getBadgeClass(subscriptionStatus, plan)}`}>
          {subscriptionStatus === "past_due" ? "Past Due" : plan}
        </span>
      </div>

      {/* Renders a payment warning alert if the subscription status is past_due */}
      {subscriptionStatus === "past_due" && (
        <div data-testid="payment-alert-past-due" className="payment-error-alert" style={{ marginBottom: "1.5rem" }}>
          <strong>Payment Failed:</strong> Your recent payment transaction failed. Please update your billing details.
        </div>
      )}

      <div className="status-details-grid">
        {/* Main plan category */}
        <div className="status-details-item">
          <span className="status-details-label">Active Plan</span>
          <span className="status-details-value">{getPlanTitle(plan)}</span>
        </div>

        {/* Display Trial countdown if the user is in the trial period */}
        {plan === "trial" && trialDaysLeft !== undefined && (
          <div className="status-details-item">
            <span className="status-details-label">Trial Expiry</span>
            <span className="status-details-value">{trialDaysLeft} days remaining</span>
          </div>
        )}

        {/* Display cycle renewal/billing date for paid subscription tiers */}
        {(plan === "pro" || plan === "team") && currentPeriodEnd !== undefined && (
          <div className="status-details-item">
            <span className="status-details-label">Renewal Date</span>
            <span className="status-details-value">{formatRenewalDate(currentPeriodEnd)}</span>
          </div>
        )}
      </div>

      {/* Render Stripe redirect errors if they occur */}
      {portalError && (
        <div data-testid="portal-error" className="payment-error-alert" style={{ marginTop: "1rem", marginBottom: "1rem" }}>
          {portalError}
        </div>
      )}

      {/* Manage billing button trigger.
          Disabled while redirect is in flight or if the user is on the Free tier. */}
      <button
        type="button"
        className="portal-redirect-button"
        onClick={handleManageBilling}
        disabled={isRedirecting || plan === "free"}
      >
        {isRedirecting ? "Redirecting..." : "Manage Billing"}
      </button>
    </div>
  );
};

/**
 * SubscriptionStatus Component
 * Wraps the status display content in a class-based ErrorBoundary to catch
 * database query or runtime exception failures.
 */
export const SubscriptionStatus: React.FC = () => {
  return (
    <SubscriptionStatusErrorBoundary>
      <SubscriptionStatusContent />
    </SubscriptionStatusErrorBoundary>
  );
};
